import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, useLocation } from 'react-router-dom';
import './index.css';
import App from './App';
import { UserProvider } from './context/UserContext';
import { RingCentralDataProvider } from './context/RingCentralDataContext';
import { CustomThemeProvider } from './themes';
import reportWebVitals from './reportWebVitals';
import { TrayProvider } from './context/TrayContext';
import { PatientProvider } from './context/PatientContext';
import { ToastProvider } from './context/ToastContext';
import {MasterDictionaryProvider} from "./context/MasterDictionaryContextType";
import {AppConfigProvider} from "./context/AppConfigContext";
import {AppAuthProvider} from "./context/AppAuthContext";
import {GridConfigProvider} from "./context/GridConfigContext";
import {EcwTabProvider} from "./context/EcwTabContext";
import { PaymentProvider } from './context/PaymentContext';
import {FavLinksProvider} from "./context/FavLinksContext";
import { MoneyBookPostMessageHandler } from './context/MoneyBookPostMessageHandler';
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { MSAL_AUTHORITY } from "./auth/msalConfig";

// Suppress known React 19 + MUI v5 compatibility warnings
// These are informational warnings from Material-UI v5 that don't affect functionality
// They will be resolved when upgrading to MUI v6 (which fully supports React 19)
if (process.env.NODE_ENV === 'development') {
    const originalWarn = console.warn;
    console.warn = (...args: any[]) => {
        const message = args[0]?.toString() || '';
        // Suppress specific MUI v5 + React 19 warnings
        if (
            message.includes('Accessing element.ref was removed in React 19') ||
            message.includes('Blocked aria-hidden on an element because its descendant retained focus') ||
            message.includes('You are providing a disabled `button` child to the Tooltip component')
        ) {
            return; // Suppress these specific warnings
        }
        originalWarn.apply(console, args);
    };
}

// Ignore benign ResizeObserver notifications (Chrome/Edge/Firefox). MUI/layout toggles (e.g. Preview ↔ Source)
// and dialog open/close animations can trigger this; it is not an app bug. This runs in both dev and production.
// Use capture + stopImmediatePropagation so CRA/webpack dev overlay does not treat it as an uncaught runtime error.
const isBenignResizeObserverMessage = (message: string) =>
    message.includes('ResizeObserver loop') ||
    message.includes('ResizeObserver loop limit exceeded') ||
    message.includes('ResizeObserver loop completed with undelivered notifications');

// Suppress ResizeObserver errors in console.error
const originalError = console.error;
console.error = (...args: any[]) => {
    const message = args[0]?.toString() || '';
    if (isBenignResizeObserverMessage(message)) {
        return; // Suppress ResizeObserver errors
    }
    // Also suppress React 19 ref warnings
    if (message.includes('Accessing element.ref was removed in React 19')) {
        return;
    }
    originalError.apply(console, args);
};

// Suppress ResizeObserver errors in the error event
window.addEventListener(
    'error',
    (event) => {
        const message = event?.message || '';
        if (!isBenignResizeObserverMessage(message)) return;
        event.preventDefault();
        event.stopImmediatePropagation();
    },
    true
);

// Also suppress in unhandledrejection (in case it's thrown as a promise rejection)
window.addEventListener(
    'unhandledrejection',
    (event) => {
        const message = event?.reason?.message || event?.reason?.toString() || '';
        if (!isBenignResizeObserverMessage(message)) return;
        event.preventDefault();
    },
    true
);


// Component that wraps App with optional RingCentralDataProvider
const ConditionalProviders: React.FC = () => {
    const location = useLocation();
    const isHospVisit = location.pathname.startsWith('/hospvisit');

    const appContent = (
        <CustomThemeProvider>
            <App />
        </CustomThemeProvider>
    );

    return isHospVisit ? appContent : (
        <RingCentralDataProvider>
            {appContent}
        </RingCentralDataProvider>
    );
};

// Component that waits for user to load and provides tray context
const WithTrayProvider: React.FC = () => {

    // if (!apexUser) {
    //     return (
    //         <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
    //             <CircularProgress />
    //         </Box>
    //     );
    // }

    return (
        <TrayProvider >
            <ConditionalProviders />
        </TrayProvider>
    );
};

// Entra ID: Application (client) ID from Azure Portal → App registrations → your app → Overview.
// Authority: your tenant URL, or https://login.microsoftonline.com/common (multi-tenant + personal accounts — see app registration "Supported account types").
const msalClientId = (process.env.REACT_APP_MSAL_CLIENT_ID || "").trim();

if (!msalClientId && process.env.NODE_ENV === "development") {
    // eslint-disable-next-line no-console
    console.warn(
        "[MSAL] Set REACT_APP_MSAL_CLIENT_ID in proj-workflow/.env to your Entra Application (client) ID. Sign-in will fail until this is set."
    );
}

const msalInstance = new PublicClientApplication({
    auth: {
        clientId: msalClientId || "00000000-0000-0000-0000-000000000000",
        authority: MSAL_AUTHORITY,
        redirectUri: window.location.origin,
    },
});

const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
);

// MSAL v3+: initialize() is required. If the user landed with #code=... (redirect or popup
// completion), handleRedirectPromise() exchanges the code and clears the hash; without this,
// the SPA keeps showing the login screen after Microsoft redirects back.
void (async () => {
    try {
        await msalInstance.initialize();
        await msalInstance.handleRedirectPromise();
    } catch (e) {
        // eslint-disable-next-line no-console
        console.error("[MSAL] initialize / handleRedirectPromise failed", e);
    }

    root.render(
    <MsalProvider instance={msalInstance}>
        <BrowserRouter
            future={{
                v7_startTransition: true,
                v7_relativeSplatPath: true,
            }}
        >
            <ToastProvider>
                <UserProvider>
                    <MoneyBookPostMessageHandler />
                    <PaymentProvider>
                        <EcwTabProvider>
                            <GridConfigProvider>
                                <AppAuthProvider>
                                    <AppConfigProvider>
                                        <MasterDictionaryProvider>
                                            <PatientProvider>
                                                <FavLinksProvider>
                                                    <WithTrayProvider />
                                                </FavLinksProvider>
                                            </PatientProvider>
                                        </MasterDictionaryProvider>
                                    </AppConfigProvider>
                                </AppAuthProvider>
                            </GridConfigProvider>
                        </EcwTabProvider>
                    </PaymentProvider>
                </UserProvider>
            </ToastProvider>
        </BrowserRouter>
    </MsalProvider>
    );
})();

reportWebVitals();
